// components/utils/MQTTMessageMapper.ts

// 1) 타입 축약/복원 매핑 추가 sensor 에서 type:type 오른쪽 type 입니다.
const typeToShort: Record<string, string> = Object.freeze({
  temperature: 'temp',
  humidity: 'humi',
  temp: 'temp',
  humi: 'humi',
  light: 'light',
  voltBat: 'voltBat',
  voltAdc: 'voltAdc',
});

const shortToType: Record<string, string> = Object.freeze({
  temp: 'temperature',
  humi: 'humidity',
  light: 'light',
  voltBat: 'voltBat',
  voltAdc: 'voltAdc',
});


// ✅ 축약 command 코드 매핑
const commandMap = Object.freeze<Record<string, string>>({
  downloadFirmware: 'df',
  setInfo: 'si',
  setOutput: 'so',
  getStatus: 'gs',
  schedule: 'sch',
  bindIO: 'bio',
  bindSensor: 'bs',
  touchInput: 'ti'
});

// ✅ 축약 operation 코드 매핑
const operationMap = Object.freeze<Record<string, string>>({
  calibration: 'cali',
});

// ✅ 역매핑
const commandReverseMap = Object.freeze<Record<string, string>>(
  Object.entries(commandMap).reduce((acc, [full, short]) => {
    acc[short] = full;
    return acc;
  }, {} as Record<string, string>)
);

const operationReverseMap = Object.freeze<Record<string, string>>(
  Object.entries(operationMap).reduce((acc, [full, short]) => {
    acc[short] = full;
    return acc;
  }, {} as Record<string, string>)
);

// ✅ publish 시: 축약 필드로 압축
export function compressMessage(original: Record<string, any>): Record<string, any> {
  const shortened: Record<string, any> = {};

  // ✅ 입력 메시지 로그 출력
  //console.log('📤 compressMessage 입력 원본:', original);
  //console.log('📤 original.typeSensor:', original.typeSensor);

  if (!original.command) {
    console.warn('❗ compressMessage: command 필드가 없습니다:', original);
  } else if (!commandMap[original.command]) {
    console.warn('❗ compressMessage: 축약 코드에 등록되지 않은 command:', original.command);
  } else {
    shortened.c = commandMap[original.command];
  }

  // sensor에 type:type 오른쪽 type
  if (original.type !== undefined) {
    const t = typeToShort[String(original.type)] || String(original.type);
    shortened.t = t; // 예: humidity -> humi
  }

  // typeSensor를 축약형으로 변환 (typeToShort 사용)
  if (original.typeSensor !== undefined) {
    const typeKey = String(original.typeSensor).toLowerCase();  // typeSensor를 문자열로 변환하여 비교
    const t = typeToShort[typeKey] || typeKey;  // typeToShort에 없으면 원래 값을 사용
    shortened.ts = t;  // 예: "humidity" -> "humi"
  }

  // ✅ 추가된 부분
  if (original.voltAdc !== undefined) shortened.voltAdc = original.voltAdc;
  if (original.voltBat !== undefined) shortened.voltBat = original.voltBat;
  //if (original.adc !== undefined) shortened.adc = original.adc;
  //if (original.bat !== undefined) shortened.bat = original.bat;

  if (original.calibration !== undefined) shortened.cali = original.calibration;
  if (original.dayOfWeek !== undefined) shortened.dw = original.dayOfWeek;
  if (original.delay !== undefined) shortened.d = original.delay;
  if (original.email) shortened.e = original.email;
  if (original.endMinutes !== undefined) shortened.end = original.endMinutes;
  if (original.fileName !== undefined) shortened.f = original.fileName;
  if (original.humidity !== undefined) shortened.humi = original.humidity;
  if (original.light !== undefined) shortened.light = original.light;
  if (original.mac) shortened.m = original.mac;
  if (original.operation) shortened.o = operationMap[original.operation] || original.operation;
  if (original.pageNo !== undefined) shortened.pn = original.pageNo;
  if (original.pinIndex !== undefined) shortened.pi = original.pinIndex;
  if (original.portNo !== undefined) shortened.n = original.portNo;
  if (original.repeatMode !== undefined) shortened.rm = original.repeatMode;
  if (original.sensorHighValue !== undefined) shortened.h = original.sensorHighValue;
  if (original.sensorLowValue !== undefined) shortened.l = original.sensorLowValue;
  if (original.slotIndex !== undefined) shortened.sI = original.slotIndex;
  if (original.startMinutes !== undefined) shortened.start = original.startMinutes;
  if (original.temperature !== undefined) shortened.temp = original.temperature;
  if (original.trigger !== undefined) shortened.tr = original.trigger === true ? 1 : original.trigger === false ? 0 : original.trigger;
  if (original.triggerValue !== undefined) shortened.tv = original.triggerValue;
  if (original.type !== undefined) shortened.t = original.type;
  if (original.typeSensor !== undefined) shortened.ts = original.typeSensor;
  if (original.value !== undefined) shortened.v = original.value === true ? 1 : original.value === false ? 0 : original.value;

  // ✅ duration 추가
  if (original.duration !== undefined) shortened.du = original.duration;

  if (original.portState) {
    shortened.ps = original.portState.map((item: any) => {
      const compressedItem: any = {};
      if (item.trigger !== undefined) compressedItem.tr = item.trigger === true ? 1 : item.trigger === false ? 0 : item.trigger;
      if (item.mac) compressedItem.m = item.mac;
      if (item.portNo !== undefined) compressedItem.n = item.portNo;
      if (item.delay !== undefined) compressedItem.d = item.delay; // ✅ 이 줄 추가
      if (item.slotIndex !== undefined) compressedItem.sI = item.slotIndex; // ✅ 필요 시 추가
      if (item.value !== undefined) compressedItem.v = item.value === true ? 1 : item.value === false ? 0 : item.value;
      // ✅ portState에도 duration 추가 가능
      if (item.duration !== undefined) compressedItem.du = item.duration;
      return compressedItem;
    });
  }

  //console.log('🧪 compressed message:', shortened);
  return shortened;
}

// ✅ subscribe 시: 축약된 필드를 원래대로 복원
export function decompressMessage(shortened: Record<string, any>): Record<string, any> {
  const expanded: Record<string, any> = {};

  // sensor 의 type
  if (shortened.t !== undefined) {
    const t = shortToType[String(shortened.t)] || String(shortened.t);
    expanded.type = t; // 예: humi -> humidity
  }

  if (shortened.c) expanded.command = commandReverseMap[shortened.c] || shortened.c;
  if (shortened.cali !== undefined) expanded.calibration = shortened.cali;
  if (shortened.d !== undefined) expanded.delay = shortened.d;
  if (shortened.du !== undefined) expanded.duration = shortened.du;
  if (shortened.dw !== undefined) expanded.dayOfWeek = shortened.dw;
  if (shortened.e) expanded.email = shortened.e;
  if (shortened.end !== undefined) expanded.endMinutes = shortened.end;
  //if (shortened.f !== undefined) expanded.fileName = shortened.f;
  if (shortened.h !== undefined) expanded.sensorHighValue = shortened.h;
  if (shortened.humi !== undefined) expanded.humidity = shortened.humi;
  if (shortened.in !== undefined) expanded.in = shortened.in;
  if (shortened.l !== undefined) expanded.sensorLowValue = shortened.l;
  if (shortened.light !== undefined) expanded.light = shortened.light;
  if (shortened.m) expanded.mac = shortened.m;
  if (shortened.m) { expanded.mac = shortened.m; }
  else if (shortened.from) {
    expanded.mac = shortened.from;   // ✅ m 없으면 from을 mac으로 처리
  }
  if (shortened.n !== undefined) expanded.portNo = shortened.n;
  if (shortened.v !== undefined) expanded.value = shortened.v;
  if (shortened.o) expanded.operation = operationReverseMap[shortened.o] || shortened.o;
  if (shortened.out !== undefined) expanded.out = shortened.out;
  if (shortened.pi !== undefined) expanded.pinIndex = shortened.pi;
  if (shortened.pn !== undefined) expanded.pageNo = shortened.pn;
  if (shortened.rm !== undefined) expanded.repeatMode = shortened.rm;
  if (shortened.si !== undefined) expanded.slotIndex = shortened.si;
  if (shortened.sI !== undefined) expanded.slotIndex = shortened.sI;
  if (shortened.index !== undefined) expanded.slotIndex = shortened.index; // ✅ 보드가 index로 보내는 경우 처리
  // ✅ 만약 보드가 sI를 안 보내고 pi만 보냈는데, 이를 slotIndex로 써야 한다면(사용자 요청):
  if (expanded.slotIndex === undefined && shortened.pi !== undefined) {
    expanded.slotIndex = shortened.pi;
  }
  if (shortened.start !== undefined) expanded.startMinutes = shortened.start;
  if (shortened.t !== undefined) expanded.type = shortened.t;
  if (shortened.ts !== undefined) expanded.typeSensor = shortened.ts;
  if (shortened.temp !== undefined) expanded.temperature = shortened.temp;
  if (shortened.tr !== undefined) expanded.trigger = shortened.tr;
  if (shortened.tv !== undefined) expanded.triggerValue = shortened.tv;
  if (shortened.v !== undefined) expanded.value = shortened.v === 1 ? true : shortened.v === 0 ? false : shortened.v;
  if (shortened.voltAdc !== undefined) expanded.voltAdc = shortened.voltAdc;
  if (shortened.voltBat !== undefined) expanded.voltBat = shortened.voltBat;
  if (shortened.ps) {
    expanded.portState = shortened.ps.map((item: any) => {
      const expandedItem: any = {};
      if (item.tr !== undefined) expandedItem.trigger = item.tr;
      if (item.d !== undefined) expandedItem.delay = item.d; // ✅ 이 줄 추가
      if (item.m) expandedItem.mac = item.m;
      if (item.n !== undefined) expandedItem.portNo = item.n;
      if (item.sI !== undefined) expandedItem.slotIndex = item.sI; // ✅ 필요 시 추가
      if (item.v !== undefined) expandedItem.value = item.v === 1 ? true : item.v === 0 ? false : item.v; // ✅ 고친 부분
      // ✅ portState에도 duration 복원
      if (item.du !== undefined) expandedItem.duration = item.du;
      return expandedItem;
    });
  }

  //console.log('🧩1 Decompressed message:', expanded);
  return expanded;
}
